import { userManager } from '@/Core/Managers'
import { UIManager } from '@/Core/Managers/UI'
import type { IUIManagerConfig } from '@/Core/Contracts/UI/UIManager'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IDrawerConfig } from '@/Core/Contracts/UI/Drawer'
import type { IListConfig } from '@/Core/Contracts/UI/List'
import { action, computed, makeObservable } from 'mobx'
import { createDrawerContext } from '../../Drawer/DrawerContextProvider'
import { createFormContext } from '../../Form/Providers'
import { createHeaderPageContext } from '../../HeaderPage/HeaderPageContextProvider'
import { createListContext } from '../../List'
import { DrawerStore } from '../../Drawer/Stores/DrawerStore'
import { FormStore } from '../../Form/Stores/FormStore'
import { ListStore } from '../../List/Stores/ListStore'
import { HeaderPageStore } from '../../HeaderPage/Stores/HeaderPageStore'

interface CrudResourceStoreConfig extends IUIManagerConfig {
    resource: IResourceConfig
    contextType?: 'list' | 'detail'
    accessMode?: string
    isFilterOpen?: boolean
    isFilter?: boolean
    isLiveCrud?: boolean
    prefilter?: Record<string, any>
    form?: any
    filter?: any
    drawer?: IDrawerConfig
    list?: IListConfig
    headerPage?: any
    actions?: Record<string, any>
}

export class CrudResourceStore extends UIManager {
    userOptions: any = null
    resource: IResourceConfig
    contextType: 'list' | 'detail' = 'list'
    isFilter: boolean = true
    prefilter: Record<string, any> = {}

    formContext: FormStore | null = null
    formComposerContext: any = null
    filterFormContext: FormStore | null = null
    listContext: ListStore | null = null
    drawerContext: DrawerStore | null = null
    parentCrudContext: CrudResourceStore | null = null
    headerPageContext: HeaderPageStore | null = null

    actions: Record<string, any> = {}

    isLiveCrud: boolean = false

    constructor({
        isFilterOpen,
        isFilter,
        isLiveCrud,
        prefilter,
        resource,
        contextType,
        form,
        filter,
        drawer,
        list,
        headerPage,
        actions,
        ...props
    }: CrudResourceStoreConfig) {
        if (!resource?.key) throw new Error(`Resource key is not defined. Resource: ${JSON.stringify(resource)}`)

        props.contextKey = 'crudContext'

        super({ ...props })

        this.resource = resource

        // Встановлюємо тип контексту
        if (contextType) {
            this.contextType = contextType
        }

        if (isLiveCrud) this.isLiveCrud = true

        // Встановлюємо actions якщо передані при ініціалізації
        if (actions && typeof actions === 'object') {
            this.actions = actions
        }

        if (!this.accessFn && typeof this.resource.accessFn === 'function') {
            // Адаптуємо AccessFunction до формату UIManager
            const resourceAccessFn = this.resource.accessFn
            this.setAccessFn(() => {
                // Можна адаптувати логіку перевірки доступу
                return resourceAccessFn({
                    crudContext: this,
                    accessMode: 'read_collection'
                })
            })
        }

        if (!this.accessFn && typeof this.resource.access === 'object') {
            const accessObj = this.resource.access
            this.setAccessFn(() => {
                // Перевіряємо доступ read за замовчуванням
                return accessObj['read_collection'] === true
            })
        }

        this.listContext = createListContext({ ...list })
        this.headerPageContext = createHeaderPageContext({ ...headerPage, storages: { resource: this.resource, crudContext: this }  })

        if (typeof isFilterOpen === 'boolean' && this.isFilterOpen === undefined) this.setIsFilterOpen(isFilterOpen)

        if (typeof isFilter === 'boolean') this.isFilter = isFilter

        if (typeof prefilter === 'object') this.prefilter = prefilter

        form.contextKey = 'formContext'
        this.formContext = createFormContext(form)
        
        filter.contextKey = 'filterFormContext'
        this.filterFormContext = createFormContext(filter)

        this.drawerContext = createDrawerContext({
            ...drawer,
            storages: { ...(drawer?.storages ?? {}), crudContext: this },
        })
        
        makeObservable(this, {
            isFilterOpen: computed,
            setIsFilterOpen: action,
        })
    }

    get isFilterOpen(): boolean {
        const isFilterOpen = userManager.getUserOption('isFilterOpen')
        return isFilterOpen === false ? false : true
    }

    setParentCrudContext(parentCrudContext: CrudResourceStore): void {
        this.parentCrudContext = parentCrudContext
    }

    setFormComposerContext(formComposerContext: any): void {
        this.formComposerContext = formComposerContext
    }

    setIsFilterOpen(isFilterOpen: boolean): void {
        if (typeof isFilterOpen === 'boolean') {
            userManager.setUserOption('isFilterOpen', isFilterOpen)
        }
    }

    setFormContext(formContext: FormStore): void {
        this.formContext = formContext
    }

    setFilterFormContext(filterFormContext: FormStore): void {
        this.filterFormContext = filterFormContext
    }

    /**
     * Перевіряє, чи це контекст списку
     * @returns true якщо це контекст списку
     */
    isList(): boolean {
        return this.contextType === 'list'
    }

    /**
     * Перевіряє, чи це контекст карточки (деталі)
     * @returns true якщо це контекст карточки
     */
    isDetail(): boolean {
        return this.contextType === 'detail'
    }

    /**
     * @deprecated Використовуйте передачу actions через конструктор замість цього методу
     * Встановлює actions для CRUD операцій
     */
    setActions(actions: Record<string, any>): void {
        this.actions = actions
    }

    /**
     * Очистка ресурсів при видаленні контексту
     * Викликається при видаленні контексту з registry
     */
    dispose(): void {
        // Можна додати логіку очищення підписок, таймерів тощо
        this.formContext = null
        this.filterFormContext = null
        this.listContext = null
        this.drawerContext = null
        this.headerPageContext = null
        this.parentCrudContext = null
    }
}
